import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart deve ser usado dentro de um CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('ematech_cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (e) {
      console.error('Erro ao carregar carrinho:', e);
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Salvar carrinho no localStorage sempre que mudar
  useEffect(() => {
    localStorage.setItem('ematech_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Adicionar item ao carrinho
  const addToCart = (product) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      
      if (existingItem) {
        // Incrementar quantidade se já existe
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Adicionar novo item
        return [...prevItems, { ...product, quantity: 1 }];
      }
    });
    
    // Mostrar o carrinho quando adicionar item
    setIsCartOpen(true);
  };

  // Remover item do carrinho
  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  // Atualizar quantidade de um item
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  // Limpar carrinho
  const clearCart = () => {
    setCartItems([]);
  };

  // Calcular total de itens
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Calcular valor total
  const totalPrice = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Gerar URL de checkout na Nuvemshop via API
  const getCheckoutUrl = async () => {
    setIsLoading(true);
    try {
      // Prioriza config.js (produção Hostgator) sobre env vars
      const apiUrl = window.EMATECH_CONFIG?.API_URL || 
                     process.env.REACT_APP_BACKEND_URL || 
                     'https://ematech-production.up.railway.app';
      
      if (cartItems.length === 0) {
        return 'https://ematech6.lojavirtualnuvem.com.br';
      }
      
      // Chamar o endpoint /criar-carrinho para criar um draft order
      const response = await fetch(`${apiUrl}/api/nuvemshop/criar-carrinho`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: cartItems.map(item => ({
            variant_id: item.variantId,
            quantity: item.quantity,
            name: item.name,
            price: item.price
          }))
        })
      });

      const data = await response.json();
      
      if (data.success && data.checkout_url) {
        // Limpar carrinho após criar pedido com sucesso
        // clearCart(); // Opcional: descomentar se quiser limpar o carrinho
        return data.checkout_url;
      } else {
        console.warn('Erro ao criar carrinho:', data.error);
        // Fallback: usa URL retornada mesmo em caso de erro parcial
        if (data.checkout_url) {
          return data.checkout_url;
        }
        return 'https://ematech6.lojavirtualnuvem.com.br';
      }
    } catch (error) {
      console.error('Erro ao gerar checkout:', error);
      // Fallback em caso de erro
      return 'https://ematech6.lojavirtualnuvem.com.br/checkout';
    } finally {
      setIsLoading(false);
    }
  };

  // Finalizar compra - redireciona para Nuvemshop
  const checkout = async () => {
    const checkoutUrl = await getCheckoutUrl();
    window.open(checkoutUrl, '_blank');
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    isLoading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
    checkout
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
